import React, { useEffect, useState } from 'react';
import { FaCalendarAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import { AiArticle } from '../../types/aiArticle';
import { getFirstValidImageUrl } from '../../utils/imageUtils';
import { richTextToString } from '../../utils/richTextUtils';
import { ArticleImage } from '../media';

import OptimizedFavoriteStarButton from './OptimizedFavoriteStarButton';

interface ArticleCardProps {
  article: AiArticle;
  showMainCreature?: boolean;
  showKeywords?: boolean;
  highlightKeyword?: string;
  showFavoriteButton?: boolean;
  className?: string;
  imageClassName?: string;
  containerClassName?: string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  showMainCreature = true,
  showKeywords = false,
  highlightKeyword,
  showFavoriteButton = false,
  className = '',
  imageClassName = '',
  containerClassName = '',
}) => {
  const [firstImageUrl, setFirstImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);

  // 日付をフォーマットする関数
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // キーワードをパースする関数
  const parseKeywords = (keywords: string | string[] | null | undefined): string[] => {
    if (!keywords) return [];

    // 配列の場合はそのまま返す
    if (Array.isArray(keywords)) {
      return keywords.filter(Boolean);
    }

    // 文字列の場合はカンマで分割
    if (typeof keywords === 'string') {
      return keywords
        .split(',')
        .map(kw => kw.trim())
        .filter(Boolean);
    }

    return [];
  };

  // 有効な画像URLを非同期で取得
  useEffect(() => {
    const fetchValidImage = async () => {
      try {
        setImageLoading(true);
        const validImageUrl = await getFirstValidImageUrl(article.images, 100, 100);
        setFirstImageUrl(validImageUrl);
      } catch (error) {
        console.warn('画像の取得に失敗しました:', error);
        setFirstImageUrl(null);
      } finally {
        setImageLoading(false);
      }
    };

    fetchValidImage();
  }, [article.images]);

  const summary = article.summary ? richTextToString(article.summary) : '';
  const keywords = parseKeywords(article.keywords);

  // documentIdがundefinedの場合はidを使用（Directus APIではidのみが返される）
  const effectiveDocumentId = article.documentId || String(article.id ?? '');

  if (!article.documentId) {
    // documentIdが存在しない場合は、idを使用するように警告を抑制
    // これはDirectus APIの仕様で、documentIdではなくidを使用する
    if (process.env.NODE_ENV === 'development') {
      // 開発環境でのみ警告を表示
      // console.log('Article.documentId is undefined, using id:', article.id);
    }
  }

  return (
    <article
      className={`bg-amber-50 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 hover:transform hover:scale-105 article-content article-card-mobile ${className}`}
    >
      {/* 記事画像 */}
      <div className="relative">
        <Link to={`/news/${effectiveDocumentId}`} className="block aspect-video overflow-hidden">
          {imageLoading ? (
            <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
              <div className="text-gray-400 text-sm">読み込み中...</div>
            </div>
          ) : (
            <ArticleImage
              src={firstImageUrl}
              alt={article.title}
              className={`hover:scale-110 transition-transform duration-300 ${imageClassName}`}
              containerClassName={`w-full h-full ${containerClassName}`}
            />
          )}
        </Link>

        {/* お気に入りボタン */}
        {showFavoriteButton && (
          <OptimizedFavoriteStarButton
            articleId={effectiveDocumentId}
            size="md"
            position="top-right"
          />
        )}
      </div>

      <div className="p-4 sm:p-6">
        {/* タイトル */}
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-3 line-clamp-2 leading-tight article-title">
          {article.title}
        </h3>

        {/* 要約 */}
        {summary && (
          <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-3 leading-relaxed article-summary">
            {summary}
          </p>
        )}

        {/* メイン生物 */}
        {showMainCreature && article.main_creature && (
          <div className="text-xs text-green-600 mb-2 font-medium truncate">
            🦕 {article.main_creature}
          </div>
        )}

        {/* メタ情報 */}
        <div className="flex items-center text-xs text-gray-500 mb-3 sm:mb-4 article-meta">
          <FaCalendarAlt className="mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">
            {formatDate(article.evaluated_at || article.publishedAt)}
          </span>
        </div>

        {/* キーワード */}
        {showKeywords && keywords.length > 0 && (
          <div
            className="mb-3 flex flex-wrap gap-1"
            role="presentation"
            onClick={e => e.preventDefault()}
            onMouseDown={e => e.stopPropagation()}
          >
            {keywords.slice(0, 3).map((kw, index) => (
              <span
                key={index}
                className={`px-2 py-1 text-xs rounded-full cursor-default ${
                  kw === highlightKeyword
                    ? 'bg-green-200 text-green-800 font-medium'
                    : 'bg-gray-100 text-gray-600'
                }`}
                role="presentation"
                onClick={e => e.stopPropagation()}
              >
                {kw}
              </span>
            ))}
            {keywords.length > 3 && (
              <span
                className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full cursor-default"
                role="presentation"
                onClick={e => e.stopPropagation()}
              >
                +{keywords.length - 3}
              </span>
            )}
          </div>
        )}

        {/* 続きを読むボタン */}
        <Link
          to={`/news/${effectiveDocumentId}`}
          className="inline-flex items-center bg-green-600 text-white px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium hover:bg-green-700 transition-colors min-h-[36px] read-more-btn"
        >
          続きを読む
        </Link>
      </div>
    </article>
  );
};

export default ArticleCard;
